import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useFlag } from '../../lib/flags';
import * as store from '../../lib/localStore';
import { trackCtaClick } from '../../lib/analytics';
import './ExitIntentModal.css';

// Single-show per visitor (90-day TTL on dismissal). Triggers when the mouse
// leaves the viewport via the top edge — a classic "about to close the tab"
// signal. Skipped on touch devices (no hover/mouseout semantics that match).

const DISMISS_KEY = 'exit-intent:shown';
const DISMISS_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MIN_DWELL_MS = 8_000;

// Don't fire on conversion-y routes where the user is already engaged in a goal.
const SKIPPED_ROUTES = ['/contact', '/careers', '/404'];

const ExitIntentModal = () => {
  const enabled = useFlag('exitIntentModal');
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const shouldRunOnThisRoute =
    enabled && !SKIPPED_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));

  useEffect(() => {
    if (!shouldRunOnThisRoute) return undefined;
    if (store.get(DISMISS_KEY)) return undefined;
    // Coarse-pointer devices (touch) almost never trigger mouseout reliably.
    if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) {
      return undefined;
    }

    let primed = false;
    const primeTimer = setTimeout(() => { primed = true; }, MIN_DWELL_MS);

    const onMouseOut = (e) => {
      if (!primed) return;
      // Mouse left the viewport via the top edge
      if (e.relatedTarget === null && e.clientY <= 0) {
        setOpen(true);
        store.set(DISMISS_KEY, '1', { ttlMs: DISMISS_TTL_MS });
        document.removeEventListener('mouseout', onMouseOut);
      }
    };
    document.addEventListener('mouseout', onMouseOut);

    return () => {
      clearTimeout(primeTimer);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [shouldRunOnThisRoute]);

  const close = useCallback((source) => {
    setOpen(false);
    trackCtaClick({ label: `exit_intent_${source}`, location: 'exit-intent' });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close('escape'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      className="tn-exit-intent"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tn-exit-intent-title"
      onClick={(e) => { if (e.target === e.currentTarget) close('backdrop'); }}
    >
      <div className="tn-exit-intent__card">
        <button
          type="button"
          className="tn-exit-intent__close"
          aria-label="Close"
          onClick={() => close('close-button')}
        >
          ×
        </button>
        <h2 id="tn-exit-intent-title" className="tn-exit-intent__title">
          Before you go —
        </h2>
        <p className="tn-exit-intent__copy">
          Want a 20-minute call with someone who's done this for a manufacturer
          before? No deck, no sales pitch. Just a sounding board.
        </p>
        <div className="tn-exit-intent__actions">
          <Link
            to="/contact"
            className="tn-exit-intent__cta"
            onClick={() => close('cta')}
          >
            Book the call
          </Link>
          <button
            type="button"
            className="tn-exit-intent__ghost"
            onClick={() => close('no-thanks')}
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentModal;
